import { canViewInBrowser, toViewableBlob } from '@/lib/viewableFile'
import { apiClient } from '@/api/client'
import {
  UPLOAD,
  UPLOAD_ALL_STAFF,
  UPLOAD_SUMMARY,
  uploadByIdPath,
  uploadDownloadPath,
  uploadExpiryPath,
  companyUploadPath,
  companyUploadByIdPath,
  companyUploadArchivePath,
  companyUploadUnarchivePath,
  companyUploadExpiryPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'

export const DOCUMENT_CATEGORIES = [
  'cv',
  'offer-letter',
  'cnic',
  'policies',
  'contracts',
  'interview-form',
  'candidate-documents',
  'onboarding-documents',
  'health-documents',
  'exit-documents',
] as const

/** Built-in or organization-defined document category. */
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number] | (string & {})
export type DocumentOwnerType = 'staff' | 'company' | 'candidate'

export interface ManagedDocumentCategory {
  id: string
  ownerType: Extract<DocumentOwnerType, 'staff' | 'company'>
  name: string
  usageCount: number
}

export interface DocumentCountByUser {
  userId: string
  /** Distinct logical document count, where one multi-file document group counts as one. */
  documentCount: number
}

/** Single document item as returned by GET /upload or GET /companies/:id/upload. */
export interface UserDocument {
  id: string
  ownerType: DocumentOwnerType
  companyId?: string
  userId?: string
  candidateId?: string
  uploadedByUserId?: string
  folderId?: string | null
  category: string
  documentType?: string
  originalName: string
  /** Whose file it is, in the Drive's read-only people views. */
  ownerName?: string
  mimeType: string
  size: number
  archivedAt: string | null
  archiveReason: string | null
  documentGroupId: string | null
  /** ISO expiry date for the document group, or null when no expiry is set. */
  expiryDate?: string | null
  createdAt?: string
}

export interface StaffDocumentOwner {
  id: string
  firstName: string
  lastName: string
  email: string
  employeeId: string
}

export interface StaffDocument extends UserDocument {
  owner: StaffDocumentOwner | null
}

// ─── Response unwrappers ─────────────────────────────────────────────────────

interface ListResponsePayload {
  message?: string
  data: UserDocument[]
}

interface StaffListResponsePayload {
  message?: string
  data: StaffDocument[]
}

interface UploadResponsePayload {
  message?: string
  data: {
    category: string
    documentGroupId: string
    files: Array<Omit<UserDocument, 'category'>>
  }
}

interface SingleDocumentPayload {
  message?: string
  data: UserDocument
}

interface SummaryResponsePayload {
  message?: string
  data: DocumentCountByUser[]
}

function unwrapList(res: unknown): UserDocument[] {
  const wrapped = res as ApiResponseWrapper<ListResponsePayload>
  const inner = wrapped?.data
  if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data
  return []
}

function unwrapUpload(res: unknown): UserDocument[] {
  const wrapped = res as ApiResponseWrapper<UploadResponsePayload>
  const inner = wrapped?.data
  if (!inner?.data?.files) return []
  const { category, documentGroupId, files } = inner.data
  return files.map((f) => ({ ...f, category, documentGroupId: documentGroupId ?? null }))
}

// ─── Legacy staff routes (/upload) ───────────────────────────────────────────

export interface ListDocumentsParams {
  userId: string
  category?: DocumentCategory
  companyId?: string
  documentGroupId?: string
  includeArchived?: boolean
  search?: string
}

export async function listDocumentsByUser(
  userId: string,
  params?: Omit<ListDocumentsParams, 'userId'>,
): Promise<UserDocument[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<ListResponsePayload>>(UPLOAD, {
    params: { userId, ...params },
  })
  return unwrapList(data)
}

export interface ListAllStaffDocumentsParams {
  category?: DocumentCategory
  companyId?: string
  includeArchived?: boolean
  search?: string
}

export async function listAllStaffDocuments(
  params?: ListAllStaffDocumentsParams,
): Promise<StaffDocument[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<StaffListResponsePayload>>(
    UPLOAD_ALL_STAFF,
    { params },
  )
  const inner = data?.data
  return Array.isArray(inner?.data) ? inner.data : []
}

export interface UploadDocumentsParams {
  category: DocumentCategory
  userId?: string
  companyId?: string
  documentGroupId?: string
}

export async function uploadDocuments(
  files: File[],
  category: DocumentCategory,
  userId?: string,
  expiryDate?: string,
  documentType?: string,
): Promise<UserDocument[]> {
  const formData = new FormData()
  for (const file of files) formData.append('files', file)
  const params: Record<string, string> = { category }
  if (userId) params.userId = userId
  if (expiryDate) params.expiryDate = expiryDate
  if (documentType) params.documentType = documentType
  const { data } = await apiClient.post<ApiResponseWrapper<UploadResponsePayload>>(
    UPLOAD,
    formData,
    { params },
  )
  return unwrapUpload(data)
}

export async function deleteDocument(id: string): Promise<void> {
  await apiClient.delete(uploadByIdPath(id))
}

/** Downloads through the API so the response is always treated as a file attachment. */
/** A file's contents, read through the authenticated route that checks access. */
export async function fetchDocumentFile(id: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(uploadDownloadPath(id), {
    responseType: 'blob',
  })
  return data
}

export async function downloadDocument(document: UserDocument): Promise<void> {
  const data = await fetchDocumentFile(document.id)
  const objectUrl = URL.createObjectURL(data)
  const anchor = window.document.createElement('a')
  anchor.href = objectUrl
  anchor.download = document.originalName
  window.document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(objectUrl)
}

/** Opens a PDF or image in a new tab; any other file type is downloaded. */
export async function openDocumentInNewTab(document: UserDocument): Promise<void> {
  const file = { name: document.originalName, mimeType: document.mimeType }
  if (!canViewInBrowser(file)) return downloadDocument(document)
  // Before the request: browsers block a tab opened after an await.
  const tab = window.open('', '_blank')
  if (!tab) return downloadDocument(document)
  tab.opener = null
  try {
    const objectUrl = URL.createObjectURL(toViewableBlob(await fetchDocumentFile(document.id), file))
    tab.location.href = objectUrl
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  } catch (error) {
    tab.close()
    throw error
  }
}

/**
 * Set (ISO date) or clear (null) the expiry date for a document group, and for
 * employee documents the type: undefined keeps it, null clears it.
 */
export async function updateDocumentExpiry(
  id: string,
  expiryDate: string | null,
  documentType?: string | null,
): Promise<UserDocument> {
  const { data } = await apiClient.patch<ApiResponseWrapper<SingleDocumentPayload>>(
    uploadExpiryPath(id),
    { expiryDate, ...(documentType !== undefined && { documentType }) },
  )
  return (data as ApiResponseWrapper<SingleDocumentPayload>).data.data
}

export async function getDocumentCountSummary(): Promise<DocumentCountByUser[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<SummaryResponsePayload>>(UPLOAD_SUMMARY)
  const inner = (data as ApiResponseWrapper<SummaryResponsePayload>)?.data
  if (inner?.data && Array.isArray(inner.data)) return inner.data
  return []
}

// ─── Company-scoped routes (/companies/:companyId/upload) ─────────────────────

export interface ListCompanyDocumentsParams {
  ownerType: DocumentOwnerType
  userId?: string
  category?: DocumentCategory
  documentGroupId?: string
  includeArchived?: boolean
  search?: string
}

export async function listCompanyDocuments(
  companyId: string,
  params: ListCompanyDocumentsParams,
): Promise<UserDocument[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<ListResponsePayload>>(
    companyUploadPath(companyId),
    { params },
  )
  return unwrapList(data)
}

export async function listCompanyDocumentCategories(
  companyId: string,
  ownerType: Extract<DocumentOwnerType, 'company' | 'staff'>,
): Promise<string[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<string[]>>(
    `${companyUploadPath(companyId)}/categories`,
    { params: { ownerType } },
  )
  return Array.isArray(data?.data) ? data.data : []
}

export async function listStaffDocumentCategories(): Promise<string[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<string[]>>(`${UPLOAD}/categories`)
  return Array.isArray(data?.data) ? data.data : []
}

export async function listManagedDocumentCategories(): Promise<ManagedDocumentCategory[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<ManagedDocumentCategory[]>>(
    `${UPLOAD}/category-management`,
  )
  return Array.isArray(data?.data) ? data.data : []
}

export async function createManagedDocumentCategory(
  ownerType: 'company' | 'staff',
  name: string,
): Promise<void> {
  await apiClient.post(`${UPLOAD}/category-management`, { ownerType, name })
}

export async function renameManagedDocumentCategory(
  id: string,
  name: string,
): Promise<void> {
  await apiClient.patch(`${UPLOAD}/category-management/${id}`, { name })
}

export async function deleteManagedDocumentCategory(id: string): Promise<void> {
  await apiClient.delete(`${UPLOAD}/category-management/${id}`)
}

export interface UploadCompanyDocumentsParams {
  ownerType: DocumentOwnerType
  /** Required unless the file goes into a Drive folder. */
  category?: DocumentCategory
  folderId?: string
  userId?: string
  documentGroupId?: string
  /** Optional ISO expiry date applied to every file in this batch. */
  expiryDate?: string
  /** Employee documents only: passport, visa… */
  documentType?: string
}

export async function uploadCompanyDocuments(
  companyId: string,
  files: File[],
  params: UploadCompanyDocumentsParams,
): Promise<UserDocument[]> {
  const formData = new FormData()
  for (const file of files) formData.append('files', file)
  const { data } = await apiClient.post<ApiResponseWrapper<UploadResponsePayload>>(
    companyUploadPath(companyId),
    formData,
    { params },
  )
  return unwrapUpload(data)
}

export async function archiveDocument(
  companyId: string,
  id: string,
  archiveReason?: string,
): Promise<UserDocument> {
  const { data } = await apiClient.patch<ApiResponseWrapper<SingleDocumentPayload>>(
    companyUploadArchivePath(companyId, id),
    { archiveReason },
  )
  return (data as ApiResponseWrapper<SingleDocumentPayload>).data.data
}

export async function unarchiveDocument(
  companyId: string,
  id: string,
): Promise<UserDocument> {
  const { data } = await apiClient.patch<ApiResponseWrapper<SingleDocumentPayload>>(
    companyUploadUnarchivePath(companyId, id),
  )
  return (data as ApiResponseWrapper<SingleDocumentPayload>).data.data
}

export async function deleteCompanyDocument(companyId: string, id: string): Promise<void> {
  await apiClient.delete(companyUploadByIdPath(companyId, id))
}

/** Set (ISO date) or clear (null) the expiry date for a company-scoped document group. */
export async function updateCompanyDocumentExpiry(
  companyId: string,
  id: string,
  expiryDate: string | null,
  documentType?: string | null,
): Promise<UserDocument> {
  const { data } = await apiClient.patch<ApiResponseWrapper<SingleDocumentPayload>>(
    companyUploadExpiryPath(companyId, id),
    { expiryDate, ...(documentType !== undefined && { documentType }) },
  )
  return (data as ApiResponseWrapper<SingleDocumentPayload>).data.data
}
